import routes from '~/routes';

/**
 * Every URL this feature talks to, built from the one route definition rather than typed out
 * again at each call site.
 *
 * `/feedback` was `/bugs_features` until recently — the old path still 301s — and the literal
 * was spelled out by hand in seven files, so the next change of that kind is seven chances to
 * miss one. Nothing would fail loudly either: a stale path answers a redirect or a 404 that
 * the page reports as "could not load".
 *
 * There are two conventions here and they are not interchangeable, which is the other reason
 * to name them. The shared request helpers take an app-absolute path, leading slash and all.
 * The shared ky instance sets prefixUrl and THROWS on a leading slash. Getting that wrong is
 * a runtime error rather than a type error, so each builder says which client it is for.
 */
type ReportId = number | string;

/**
 * The detail path for one report, from the single route definition.
 *
 * @param {ReportId} id The report's id.
 * @return {string} An app-absolute path.
 */
const detail = (id: ReportId): string =>
  routes.bugsFeatureDetailPath.replace(':id', String(id));

/**
 * ky sets prefixUrl and throws on a leading slash, so its paths are relative.
 *
 * @param {string} path An app-absolute path.
 * @return {string} The same path without its leading slash.
 */
const forKy = (path: string): string => path.replace(/^\//, '');

/**
 * For `<Link to>` and the shared request helpers.
 *
 * @param {ReportId} id The report's id.
 * @return {string} An app-absolute path.
 */
export const reportPath = (id: ReportId): string => detail(id);

/**
 * @param {ReportId} id The report's id.
 * @return {string} Where a resolution is recorded or reopened.
 */
export const resolutionPath = (id: ReportId): string =>
  `${detail(id)}/resolution`;

/**
 * @param {ReportId} id The report's id.
 * @return {string} Where a comment is posted.
 */
export const commentsPath = (id: ReportId): string => `${detail(id)}/comments`;

/**
 * @param {ReportId} id The report's id.
 * @return {string} Where the description is edited.
 */
export const descriptionPath = (id: ReportId): string =>
  `${detail(id)}/description`;

/**
 * @param {ReportId} id The report's id.
 * @return {string} Where the Jira fields are written.
 */
export const jiraFieldsPath = (id: ReportId): string =>
  `${detail(id)}/jira_fields`;

/**
 * @param {ReportId} id The report's id.
 * @return {string} The report itself, for the shared ky instance.
 */
export const reportJsonPath = (id: ReportId): string =>
  forKy(`${detail(id)}.json`);

/**
 * @param {ReportId} id The report's id.
 * @return {string} The editable Jira fields, for the shared ky instance.
 */
export const jiraFieldsJsonPath = (id: ReportId): string =>
  forKy(`${jiraFieldsPath(id)}.json`);

/**
 * @return {string} This week's on-call engineer, for the shared ky instance.
 */
export const oncallJsonPath = (): string =>
  forKy(`${routes.bugsFeaturesPath}/oncall.json`);

/**
 * The Jira comment thread. For the request helpers, so app-absolute.
 *
 * Named for its client like the rest, because this is the one that got it wrong: it was
 * introduced as a ky path and handed to request.get, which does not prefix — so every URL
 * came out as "http://example.comfeedback/42/jira_comments.json", with no slash, and the
 * thread simply never loaded. The panel degrades quietly on a failed read, so nothing said
 * so; it surfaced only as an unhandled request in the test run.
 *
 * @param {ReportId} id The report's id.
 * @return {string} An app-absolute path.
 */
export const jiraCommentsJsonPath = (id: ReportId): string =>
  `${detail(id)}/jira_comments.json`;
